import { itemField, type Row, upper } from '../ability/context';
import { mountedTrapAbilityIds } from './mounted';
import { readLayerListEntryUuid } from '../compile/inventory-raw-readers';
import { ftTrapDamageDiceTable } from '../port/k3-rules/trap_craft_tables_inc';
import { phpJsonInt, phpJsonTruthy } from '../compile/php-json-values';
import { isUuid } from '../reactions/layer-list-ids';

export interface TrapMountReaders {
  inventoryField(row: Row, key: string): unknown;
  value(row: Row, key: string): unknown;
  entries(row: Row, key: string): unknown[];
}
export interface TrapMountInputs {
  inventory: Row;
  candidates: string[];
  members: Row[];
}
interface MountSnapshot { enchantment_template_id: string; inventory_inspection_dc: number }
export interface TrapMountCommand {
  kind: 'mount' | 'refresh';
  before: MountSnapshot;
  after: MountSnapshot | Record<string, never>;
}
export type TrapMountPlan = { ok: true; command: TrapMountCommand } | { ok: false; message: string };

/** Check final reader/planner values for both mount and unchanged refresh before the producer commits. */
function finalMountPlan(command: TrapMountCommand): TrapMountPlan {
  const snapshots = command.kind === 'refresh' ? [command.before] : [command.before, command.after as MountSnapshot];
  for (const row of snapshots) {
    if (row.enchantment_template_id !== '' && !isUuid(row.enchantment_template_id))
      return { ok: false, message: 'trap-mount:invalid-template' };
    if (!Number.isSafeInteger(row.inventory_inspection_dc)) return { ok: false, message: 'trap-mount:invalid-dc' };
  }
  return { ok: true, command };
}

/** Craft Trap's authored DC wins; otherwise reverse-map the native dice table in its original order. */
export function trapMountPrefillDc(ability: Row, value: TrapMountReaders['value']): number {
  const authored = phpJsonInt(value(ability, 'ability_dc'));
  if (authored > 0 && authored !== 10) return Math.max(5, Math.min(30, authored));
  const dice = phpJsonInt(value(ability, 'dice')), die = phpJsonInt(value(ability, 'die'));
  for (const [tier, severities] of ftTrapDamageDiceTable()) {
    for (const [severity, pair] of Object.entries(severities as Record<string, number[]>)) {
      if (pair[0] !== dice || pair[1] !== die) continue;
      return Math.max(5, Math.min(30, 9 + Number(tier) * 3
        + (severity === 'deadly' ? 2 : severity === 'dangerous' ? 1 : 0)
        + (phpJsonInt(value(ability, 'aoe_distance')) >= 20 ? 1 : 0)
        + (phpJsonTruthy(value(ability, 'persistent_aoe')) ? 1 : 0)));
    }
  }
  return 10;
}

/** All eligibility and stamp choices belong to the browser. Facts contain only saved rows. */
export async function planTrapMount(host: Row, ability: Row, facts: TrapMountInputs, read: TrapMountReaders,
  eligible: (itemId: string, templateId: string) => Promise<{ valid: boolean; message: string }>): Promise<TrapMountPlan> {
  const refuse = (message: string): TrapMountPlan => ({ ok: false, message });
  const inventoryId = upper(read.inventoryField(host, 'inventory_id'));
  if (!inventoryId || inventoryId !== upper(read.inventoryField(facts.inventory, 'inventory_id')))
    return refuse('trap-mount:placed-object-facts-missing');
  const before = { enchantment_template_id: upper(read.inventoryField(facts.inventory, 'enchantment_template_id')),
    inventory_inspection_dc: phpJsonInt(read.inventoryField(facts.inventory, 'inventory_inspection_dc')) };
  if (upper(read.inventoryField(host, 'enchantment_template_id')) !== before.enchantment_template_id
    || upper(read.inventoryField(host, 'inventory_item_id') ?? itemField(host, 'item_id')) !== upper(read.inventoryField(facts.inventory, 'inventory_item_id')))
    return refuse('trap-mount:object-changed-during-read');
  const abilityId = upper(itemField(ability, 'item_id'));
  const mounted = mountedTrapAbilityIds({ ...host, enchantment_template_enchantments: facts.members.filter(row =>
    upper(row.enchantment_template_id) === before.enchantment_template_id && !phpJsonInt(row.enchantment_template_enchantment_deleted)) },
    (row, key) => read.entries(row, key).map(entry => readLayerListEntryUuid(entry)));
  if (mounted.includes(abilityId)) return finalMountPlan({ kind: 'refresh', before, after: {} });
  if (mounted.length) return refuse('This object already carries a different trap — disarm and remove it first.');
  if (before.enchantment_template_id) return refuse('This object already carries an enchantment — it cannot also hold a trap.');
  const template = facts.candidates.find(id => {
    const members = facts.members.filter(row => upper(row.enchantment_template_id) === upper(id));
    return members.length === 1 && members[0]!.enchantment_type === 'trap_ability' && upper(members[0]!.enchantment_item_id) === abilityId;
  });
  if (!template) return refuse('No trap template wraps this trap ability — craft it with the Craft Trap tool first.');
  const itemId = upper(read.inventoryField(facts.inventory, 'inventory_item_id') ?? itemField(host, 'item_id'));
  const verdict = await eligible(itemId, template);
  if (!verdict.valid) return refuse(verdict.message);
  const rawDc = read.inventoryField(facts.inventory, 'inventory_inspection_dc');
  const dc = rawDc == null || rawDc === '' || before.inventory_inspection_dc === 10
    ? trapMountPrefillDc(ability, read.value) : before.inventory_inspection_dc;
  return finalMountPlan({ kind: 'mount', before,
    after: { enchantment_template_id: upper(template), inventory_inspection_dc: dc } });
}
